import React, { useState } from 'react';

import {
  Button,
  QuantaInput,
  Label,
  QuantaFormik,
  QuantaForm,
  Text,
  Chip,
  useResponsiveParams,
  useQuantaFormikContext,
} from 'quanta-ui';

import { useMutation } from '@apollo/client';
import { SEND_QUOTE_EMAIL_PCV_ALERT } from '../../queries';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

export default function EmailFragment({
  PCVQuoteInput,
  goBack,
  initialEmail,
}) {
  const [sentSuccess, setSentSuccess] = useState(true);
  const [loading, setLoading] = useState(false);
  const [displayMessage, setDisplayMessage] = useState(null);

  const [doSendEmail] = useMutation(
    SEND_QUOTE_EMAIL_PCV_ALERT,
    {
      onCompleted: (data) => {
        setSentSuccess(true);
        setDisplayMessage(data[`${Object.keys(data)[0]}`]?.message);
        setLoading(false);
      },
      onError: (_) => {
        setSentSuccess(false);
        setDisplayMessage(
          'Email send failed...Please try again after some time.',
        );
        setLoading(false);
      },
    },
  );

  const onSubmit = async (values, { setFieldValue }) => {
    try {
      setLoading(true);
      setDisplayMessage(null);

      let variables = null;
      if (values?.ccEmail && !validate(values)?.ccEmail) {
        setFieldValue(`ccEmailIDs`, [
          ...values?.ccEmailIDs,
          values?.ccEmail,
        ]);
        setFieldValue(`ccEmail`, '');
        variables = {
          ...TWQuoteInput,
          email: values?.email,
          ccEmailIds: [...values?.ccEmailIDs, values?.ccEmail],
        };
      } else {
        variables = {
          ...TWQuoteInput,
          email: values?.email,
          ccEmailIds: values?.ccEmailIDs,
        };
      }
      if (SEND_QUOTE_EMAIL_PCV_ALERT) {
        doSendEmail({
          variables,
        });
      }
    } catch (error) {
      setSentSuccess(false);
      setDisplayMessage(
        'Email send failed...Please try again after some time.',
      );
      setLoading(false);
    }
  };

  const initialValues = {
    email: initialEmail ? initialEmail : '',
    ccEmailIDs: [],
    ccEmail: '',
  };

  const messageSize = useResponsiveParams({
    for_iPad: 'large',
    for_mobile: 'normal',
  });

  const validate = (values) => {
    const errors = {};
    if (typeof values.email !== 'undefined') {
      var email_check = new RegExp(
        /^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i,
      );
      if (!values.email) {
        errors.email = '* Required';
      } else if (!email_check.test(values.email)) {
        errors.email = '* Please enter valid email address.';
      }
      if (values?.ccEmail && !email_check.test(values?.ccEmail)) {
        errors.ccEmail = '* Please enter valid email address.';
      } else if (
        values?.ccEmail &&
        (values?.ccEmail?.trim()?.toLowerCase() ===
          values?.email?.trim()?.toLowerCase() ||
          values?.ccEmailIDs?.find(
            (ccEmailId) =>
              ccEmailId?.trim()?.toLowerCase() ===
              values?.ccEmail?.trim()?.toLowerCase(),
          ))
      ) {
        errors.ccEmail = '* This email ID is already added';
      }
    }
    return errors;
  };

  const SendEmailForm = ({ initialValues, validate, onSubmit }) => {
    const { values, setFieldValue } = useQuantaFormikContext();
    return (
      <QuantaForm>
        <QuantaInput
          name="email"
          size="full"
          type="email"
          placeholder="Please enter your email"
          disabled={loading}
          label={
            <Text type={'label'}>
              {' '}
              Email ID <Text severity="error">*</Text>
            </Text>
          }
        />
        <QuantaInput
          name="ccEmail"
          type={'email'}
          label={<Text type={'label'}> CC/Reference Email IDs </Text>}
          size={'full'}
          placeholder={'Enter CC/Reference Email IDs'}
          disabled={loading}
          onKeyDown={(evt) => {
            if (
              ['tab', 'enter', ',', ';']?.includes(
                evt?.key?.toString()?.trim()?.toLowerCase(),
              )
            ) {
              evt.preventDefault();
              if (values?.ccEmail && !validate(values)?.ccEmail) {
                setFieldValue(`ccEmailIDs`, [
                  ...values?.ccEmailIDs,
                  values?.ccEmail?.trim(),
                ]);
                setFieldValue(`ccEmail`, '');
              }
            }
          }}
          onPaste={(evt) => {
            evt.preventDefault();
            const paste = evt.clipboardData.getData('text');
            const emails = paste.match(
              /[\w\d\.-]+@[\w\d\.-]+\.[\w\d\.-]+/g,
            );
            if (emails) {
              const toBeAdded = emails.filter(
                (email) =>
                  !values?.ccEmailIDs?.includes(email) &&
                  email !== values?.email,
              );
              setFieldValue(`ccEmailIDs`, [
                ...values?.ccEmailIDs,
                ...toBeAdded,
              ]);
            }
          }}
        />
        {values?.ccEmailIDs?.length > 0 && (
          <Text className="flex gap-3 flex-wrap">
            {values?.ccEmailIDs?.map((ccEmailId, idx) => {
              return (
                <Chip
                  key={idx}
                  onRemove={(_) =>
                    setFieldValue(`ccEmailIDs`, [
                      ...values?.ccEmailIDs?.slice(0, idx),
                      ...values?.ccEmailIDs?.slice(idx + 1),
                    ])
                  }
                  isChipDisabled={loading}
                >
                  {ccEmailId}
                </Chip>
              );
            })}
          </Text>
        )}
        {displayMessage && !loading ? (
          <Text severity={sentSuccess ? 'success' : 'error'}>
            {displayMessage}
          </Text>
        ) : (
          <div>&nbsp;</div>
        )}
        <div className="flex flex-row justify-end pt-4 space-x-4">
          <Button
            size="small"
            flat
            type={'button'}
            className="w-24"
            onClick={() => goBack()}
          >
            Back
          </Button>
          <Button
            size="small"
            type={'submit'}
            className="w-24"
            iconPre={
              loading ? (
                <FontAwesomeIcon
                  icon={faSpinner}
                  spin={true}
                  className="mr-2"
                />
              ) : undefined
            }
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send Email'}
          </Button>
        </div>
      </QuantaForm>
    );
  };

  return (
    <QuantaFormik
      enableReinitialise={true}
      {...{ initialValues, validate, onSubmit }}
    >
      <SendEmailForm {...{ initialValues, validate, onSubmit }} />
    </QuantaFormik>
  );
}
